import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AppraisalInfo,
  BorrowerAction,
  CompleteActionRequest,
  CompleteActionResponse,
  ENVIRONMENT,
  GiftLetterInfo,
  InsuranceEligibility,
  InsuranceInfo,
  InsuranceRedirectResponse,
  LoadAppraisalResponse,
  LoadBorrowerActionsResponse,
  LoadInsuranceResponse,
  StateLicenseVerbiage,
  SubmitGiftLetterRequest,
  SubmitGiftLetterResponse,
  SubmitInsuranceRequest,
  SubmitInsuranceResponse,
  SubmitPaymentRequest,
  SubmitPaymentResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class BorrowerActionsService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async loadActions(loanId: string, borrowerId: string): Promise<LoadBorrowerActionsResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/actions`;

    try {
      return await firstValueFrom(this.http.get<LoadBorrowerActionsResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error loading actions for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to load borrower actions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getAction(loanId: string, actionId: string): Promise<BorrowerAction> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/actions/${actionId}`;

    try {
      return await firstValueFrom(this.http.get<BorrowerAction>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error getting action ${actionId}`);
      throw new Error(`Failed to get action: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async completeAction(loanId: string, request: CompleteActionRequest): Promise<CompleteActionResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/actions/${request.actionId}/complete`;

    try {
      return await firstValueFrom(this.http.post<CompleteActionResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error completing action ${request.actionId}`);
      throw new Error(`Failed to complete action: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async updateAwcViewed(loanId: string, borrowerId: string): Promise<boolean> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/awcviewed`;

    try {
      return await firstValueFrom(this.http.put<boolean>(url, null));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error updating AWC viewed for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to update AWC viewed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async submitPayment(request: SubmitPaymentRequest): Promise<SubmitPaymentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${request.loanId}/payments`;

    try {
      return await firstValueFrom(this.http.post<SubmitPaymentResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error submitting payment for loan ${request.loanId}`);
      throw new Error(`Failed to submit payment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async loadInsurance(loanId: string): Promise<LoadInsuranceResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/insurance`;

    try {
      return await firstValueFrom(this.http.get<LoadInsuranceResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error loading insurance for loan ${loanId}`);
      throw new Error(`Failed to load insurance: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async submitInsurance(request: SubmitInsuranceRequest): Promise<SubmitInsuranceResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${request.loanId}/insurance`;

    try {
      return await firstValueFrom(this.http.post<SubmitInsuranceResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error submitting insurance for loan ${request.loanId}`);
      throw new Error(`Failed to submit insurance: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async updateInsurance(insuranceId: string, request: SubmitInsuranceRequest): Promise<InsuranceInfo> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${request.loanId}/insurance/${insuranceId}`;

    try {
      return await firstValueFrom(this.http.put<InsuranceInfo>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error updating insurance ${insuranceId}`);
      throw new Error(`Failed to update insurance: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async loadGiftLetters(loanId: string): Promise<GiftLetterInfo[]> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/giftletters`;

    try {
      return await firstValueFrom(this.http.get<GiftLetterInfo[]>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error loading gift letters for loan ${loanId}`);
      throw new Error(`Failed to load gift letters: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async submitGiftLetter(request: SubmitGiftLetterRequest): Promise<SubmitGiftLetterResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${request.loanId}/giftletters`;

    try {
      return await firstValueFrom(this.http.post<SubmitGiftLetterResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error submitting gift letter for loan ${request.loanId}`);
      throw new Error(`Failed to submit gift letter: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async loadAppraisal(loanId: string): Promise<LoadAppraisalResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/appraisal`;

    try {
      return await firstValueFrom(this.http.get<LoadAppraisalResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error loading appraisal for loan ${loanId}`);
      throw new Error(`Failed to load appraisal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getAppraisal(loanId: string): Promise<AppraisalInfo | null> {
    const response = await this.loadAppraisal(loanId);
    return response.appraisal;
  }

  async getInsuranceRedirectUrl(loanId: string, borrowerId: string): Promise<InsuranceRedirectResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/insurance/getRedirectUrl`;

    try {
      return await firstValueFrom(this.http.get<InsuranceRedirectResponse>(url));
    } catch (error: unknown) {
      const message = this.extractErrorMessage(error);
      this.loggingService.logException(error, `Error getting insurance redirect for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to get insurance redirect: ${message}`);
    }
  }

  async getInsuranceEligibility(loanId: string): Promise<InsuranceEligibility> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/insurance/eligibility`;

    try {
      return await firstValueFrom(this.http.get<InsuranceEligibility>(url));
    } catch (error: unknown) {
      const message = this.extractErrorMessage(error);
      this.loggingService.logException(error, `Error checking insurance eligibility for loan ${loanId}`);
      throw new Error(`Failed to check insurance eligibility: ${message}`);
    }
  }

  async hideInsuranceTask(loanId: string, borrowerId: string): Promise<boolean> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/insurance/hide`;

    try {
      return await firstValueFrom(this.http.patch<boolean>(url, null));
    } catch (error: unknown) {
      const message = this.extractErrorMessage(error);
      this.loggingService.logException(error, `Error hiding insurance task for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to hide insurance task: ${message}`);
    }
  }

  async getMelloInsurancePortalUrl(): Promise<InsuranceRedirectResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/insurance`;

    try {
      const response = await firstValueFrom(this.http.post<{ portalUrlWithToken: string }>(url, {}));
      return { redirectUrl: response.portalUrlWithToken };
    } catch (error: unknown) {
      const message = this.extractErrorMessage(error);
      this.loggingService.logException(error, 'Error getting Mello insurance portal URL');
      throw new Error(`Failed to get Mello insurance portal URL: ${message}`);
    }
  }

  /**
   * Get state license verbiage for insurance disclosures.
   *
   * @param loanId - The loan identifier
   * @param borrowerId - The borrower identifier
   * @returns Promise of StateLicenseVerbiage
   */
  async getStateLicenseVerbiage(loanId: string, borrowerId: string): Promise<StateLicenseVerbiage> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/insurance/getStateVerbiage`;

    try {
      return await firstValueFrom(this.http.get<StateLicenseVerbiage>(url));
    } catch (error: unknown) {
      const message = this.extractErrorMessage(error);
      this.loggingService.logException(error, `Error getting state license verbiage for loan ${loanId}`);
      throw new Error(`Failed to get state license verbiage: ${message}`);
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as {
        error?: { messages?: string[]; errorMessage?: string; message?: string };
        message?: string;
      };
      const errorBody = httpError.error;

      if (errorBody) {
        if (Array.isArray(errorBody.messages) && errorBody.messages.length > 0) {
          return errorBody.messages.join('. ');
        }
        if (errorBody.errorMessage) {
          return errorBody.errorMessage;
        }
        if (errorBody.message) {
          return errorBody.message;
        }
      }

      if (httpError.message) {
        return httpError.message;
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected error occurred';
  }
}
